import React, { useState, useMemo, useEffect } from "react";
import apiClient from "../utils/apiClient";
import { X, Search, Check, Filter } from "lucide-react";
import server from "../utils/serverConfig";
import toast from "react-hot-toast";
import { useLtpStore } from "../contexts/LTPStore/useLTPStore";
import WebSocketManager from "../contexts/websocketManager";

const PledgePlusArbBulkOrderModal = ({ isOpen, onClose, users, userMappings, onSuccess }) => {
    const [formData, setFormData] = useState({
        symbol: "",
        arbPercentage: "",
        bufferPercentage: "",
        lots: "1",
    });

    const [searchQuery, setSearchQuery] = useState("");
    const [selectedClients, setSelectedClients] = useState(new Set());
    const [submitting, setSubmitting] = useState(false);
    const [brokerFilter, setBrokerFilter] = useState("ALL");

    // LTP and Subscription state
    const currentSymbol = formData.symbol?.trim()?.toUpperCase();
    const ltp = useLtpStore((state) => state.ltps[currentSymbol]);
    const [subscribing, setSubscribing] = useState(false);

    const handleSubscribe = async () => {
        if (!currentSymbol) return;
        setSubscribing(true);
        try {
            await WebSocketManager.getInstance().subscribeToSymbols([currentSymbol]);
            toast.success(`Subscribed to ${currentSymbol}`);
        } catch (err) {
            toast.error("Subscription failed");
        } finally {
            setSubscribing(false);
        }
    };

    // Reset selection when modal opens
    useEffect(() => {
        if (isOpen) {
            setSelectedClients(new Set());
        }
    }, [isOpen]);

    // Filter valid clients (Active & Multiplier > 0)
    const eligibleClients = useMemo(() => {
        if (!users || !userMappings) return [];

        return users.filter(u => {
            if ((u.status || "").toLowerCase() !== "active") return false;

            const broker = (u.broker || "").toLowerCase();
            const validBrokers = [
                "zerodha"
            ];
            if (!validBrokers.some(vb => broker.includes(vb))) return false;

            let mapping = userMappings.find(m =>
                (m.client_id || m.clientId) === (u.client_id || u.clientId) &&
                (m.broker || "").toLowerCase() === broker
            );

            if (!mapping) {
                mapping = userMappings.find(m => (m.client_id || m.clientId) === (u.client_id || u.clientId));
            }
            if (!mapping) return false;

            const isPP = u.is_pledge_plus === true || String(u.is_pledge_plus).toLowerCase() === 'true';
            const isPPMini = u.is_pledgeplus_mini === true || String(u.is_pledgeplus_mini).toLowerCase() === 'true';
            if (!isPP && !isPPMini) return false;

            return true;
        }).map(u => {
            let multiplier = 1;
            const broker = (u.broker || "").toLowerCase();
            let mapping = userMappings.find(m =>
                (m.client_id || m.clientId) === (u.client_id || u.clientId) &&
                (m.broker || "").toLowerCase() === broker
            );
            if (!mapping) {
                mapping = userMappings.find(m => (m.client_id || m.clientId) === (u.client_id || u.clientId));
            }

            if (mapping) {
                const isPPMini = u.is_pledgeplus_mini === true || String(u.is_pledgeplus_mini).toLowerCase() === 'true';
                const futKey = isPPMini ? 'pledgemini_mcx_multiplier' : 'pledgeplus_mcx_multiplier';
                const futVal = Number(mapping[futKey]);
                multiplier = futVal > 0 ? futVal : 1;
            }
            return { ...u, currentMultiplier: multiplier };
        });
    }, [users, userMappings]);

    const filteredClients = useMemo(() => {
        let list = eligibleClients;
        if (brokerFilter !== "ALL") {
            list = list.filter(c => (c.broker || "").toLowerCase() === brokerFilter.toLowerCase());
        }
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            list = list.filter(c =>
                (c.name || "").toLowerCase().includes(q) ||
                (c.clientId || c.client_id || "").toLowerCase().includes(q) ||
                (c.label || "").toLowerCase().includes(q)
            );
        }
        return list;
    }, [eligibleClients, brokerFilter, searchQuery]);

    const uniqueLabels = useMemo(() => {
        const labels = new Set();
        eligibleClients.forEach(c => {
            if (c.label) labels.add(c.label);
        });
        return Array.from(labels).sort();
    }, [eligibleClients]);

    const toggleSelectAll = () => {
        if (selectedClients.size === filteredClients.length) {
            setSelectedClients(new Set());
        } else {
            setSelectedClients(new Set(filteredClients.map(c => `${c.clientId || c.client_id}_${c.broker || "fyers"}`)));
        }
    };

    const toggleClientSelection = (uniqueId) => {
        const next = new Set(selectedClients);
        if (next.has(uniqueId)) next.delete(uniqueId);
        else next.add(uniqueId);
        setSelectedClients(next);
    };

    const toggleLabelSelection = (label) => {
        const labelClients = filteredClients.filter(c => c.label === label);
        if (labelClients.length === 0) return;

        const labelIds = labelClients.map(c => `${c.clientId || c.client_id}_${c.broker || "fyers"}`);
        const allSelected = labelIds.every(id => selectedClients.has(id));

        const next = new Set(selectedClients);
        if (allSelected) {
            labelIds.forEach(id => next.delete(id));
        } else {
            labelIds.forEach(id => next.add(id));
        }
        setSelectedClients(next);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (selectedClients.size === 0) {
            toast.error("Please select at least one client");
            return;
        }
        if (!formData.symbol) {
            toast.error("Please specify Symbol");
            return;
        }
        if (formData.arbPercentage === "" || formData.arbPercentage === null) {
            toast.error("Please specify Arb Percentage");
            return;
        }
        if (formData.bufferPercentage === "" || formData.bufferPercentage === null) {
            toast.error("Please specify Buffer Percentage");
            return;
        }
        if (!formData.lots || Number(formData.lots) <= 0) {
            toast.error("Please specify a valid Lot quantity");
            return;
        }

        setSubmitting(true);
        try {
            const baseLots = Number(formData.lots) || 1;
            const orderPayload = Array.from(selectedClients).map(uniqueId => {
                const client = eligibleClients.find(c => `${c.clientId || c.client_id}_${c.broker || "fyers"}` === uniqueId);
                const clientId = client.clientId || client.client_id;
                const multiplier = client?.currentMultiplier || 1;
                const totalQty = baseLots * multiplier;

                return {
                    client_id: clientId,
                    symbol: formData.symbol.trim().toUpperCase(),
                    arb_percentage: Number(formData.arbPercentage),
                    buffer_percentage: Number(formData.bufferPercentage),
                    lots: baseLots,
                    qty: totalQty,
                    broker: client?.broker || "fyers"
                };
            });

            const dhanXtsPayload = orderPayload.filter(o => o.broker === "dhan_xts" || o.broker === "dhan_ss");
            const dhanXtsAnuragPayload = orderPayload.filter(o => o.broker === "dhan_xts_anurag" || o.broker === "dhan_ss_anurag" || o.broker === "dhan_anurag");
            const otherBrokersPayload = orderPayload.filter(o => !["dhan_xts", "dhan_ss", "dhan_xts_anurag", "dhan_ss_anurag", "dhan_anurag"].includes(o.broker));

            const flattenResponse = (apiData) => {
                if (!apiData || !Array.isArray(apiData)) return [];
                const flattened = [];
                apiData.forEach(clientEntry => {
                    if (clientEntry.order_resp && Array.isArray(clientEntry.order_resp)) {
                        clientEntry.order_resp.forEach(order => {
                            flattened.push({
                                clientId: clientEntry.client_id,
                                symbol: order.symbol || formData.symbol,
                                qty: order.qty,
                                status: order.status === "failed" ? "Rejected" : (order.status || "Pending"),
                                rejectReason: order.response?.message || order.response?.error || order.message || "",
                                orderId: order.response?.id || order.id || "N/A",
                                broker: eligibleClients.find(c => (c.clientId || c.client_id) === clientEntry.client_id)?.broker || "fyers"
                            });
                        });
                    } else {
                        const execResp = clientEntry.execution_response || clientEntry;
                        const status = execResp.status === "success" ? "Success" : (execResp.status || "Pending");
                        flattened.push({
                            clientId: clientEntry.client_id,
                            symbol: clientEntry.stock_name || formData.symbol,
                            qty: clientEntry.lots || formData.lots,
                            status: status,
                            rejectReason: execResp.reason || execResp.message || "",
                            orderId: execResp.spot_order_id || execResp.id || "N/A",
                            broker: clientEntry.broker || "zerodha"
                        });
                    }
                });
                return flattened;
            };

            let finalOrders = [];
            const apiCalls = [];

            if (otherBrokersPayload.length > 0) {
                apiCalls.push(
                    apiClient.post(`${server.godseye.baseUrl}cash-carry-order-placement`, otherBrokersPayload)
                        .then(res => flattenResponse(res.data.data))
                        .catch(err => {
                            if (err.response?.data?.data) {
                                toast.error(err.response.data.message || "Arb bulk order placement had issues");
                                return flattenResponse(err.response.data.data);
                            }
                            throw err;
                        })
                );
            }

            if (dhanXtsPayload.length > 0) {
                apiCalls.push(
                    apiClient.post(`${server.ss_dhan.baseUrl}order-placement-xts`, dhanXtsPayload)
                        .then(res => flattenResponse(res.data.data))
                        .catch(err => {
                            if (err.response?.data?.data) {
                                toast.error(err.response.data.message || "XTS Arb bulk order placement had issues");
                                return flattenResponse(err.response.data.data);
                            }
                            throw err;
                        })
                );
            }

            if (dhanXtsAnuragPayload.length > 0) {
                apiCalls.push(
                    apiClient.post(`${server.ss_dhan_anurag.baseUrl}order-placement-xts`, dhanXtsAnuragPayload)
                        .then(res => flattenResponse(res.data.data))
                        .catch(err => {
                            if (err.response?.data?.data) {
                                toast.error(err.response.data.message || "XTS Anurag Arb bulk order placement had issues");
                                return flattenResponse(err.response.data.data);
                            }
                            throw err;
                        })
                );
            }

            try {
                const results = await Promise.all(apiCalls);
                for (const result of results) {
                    finalOrders = [...finalOrders, ...result];
                }
                toast.success(`Arb Bulk orders submitted successfully`);
            } catch (err) {
                throw err;
            }

            if (finalOrders.length > 0) {
                if (onSuccess) onSuccess(finalOrders);
                onClose();
            }
        } catch (err) {
            console.error("❌ Arb bulk order placement failed:", err);
            toast.error("Arb bulk order placement failed: " + (err.response?.data?.detail || err.message));
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] backdrop-blur-sm p-4">
            <div className="bg-[#1a1d29] rounded-xl border border-gray-700 w-full max-w-5xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">

                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-700 flex justify-between items-center bg-[#14161f]">
                    <div>
                        <h2 className="text-white text-xl font-bold">Arb Bulk Order Placement</h2>
                        <p className="text-gray-400 text-xs mt-1">Place manual Arb orders with Symbol, Arb %, Buffer %, and Lots</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-full text-gray-400 transition-colors">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="flex-1 overflow-auto p-6 flex flex-col lg:flex-row gap-6">

                    {/* Form Side */}
                    <div className="lg:w-1/2 space-y-4 flex flex-col overflow-hidden">
                        <div className="flex-1 overflow-y-auto pr-2 sleek-scrollbar space-y-4">
                            <h3 className="text-blue-400 font-semibold text-sm uppercase tracking-wider mb-4 border-b border-blue-900/30 pb-2">Arb Order Parameters</h3>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <div className="flex justify-between items-end mb-1.5">
                                        <label className="block text-gray-400 text-xs font-medium uppercase">Symbol <span className="text-red-500">*</span></label>
                                        <div className="flex items-center gap-2">
                                            {currentSymbol && (
                                                <>
                                                    {ltp !== undefined ? (
                                                        <div className="flex items-center gap-1.5 px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 rounded">
                                                            <span className="text-[10px] text-blue-400 font-bold uppercase">LTP:</span>
                                                            <span className="text-xs text-white font-mono font-bold">{ltp.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={handleSubscribe}
                                                            disabled={subscribing}
                                                            className="text-[10px] bg-orange-500/10 border border-orange-500/30 text-orange-400 font-bold px-2 py-0.5 rounded hover:bg-orange-500/20 transition-all flex items-center gap-1"
                                                        >
                                                            {subscribing ? <div className="w-2 h-2 border border-orange-400/30 border-t-orange-400 rounded-full animate-spin"></div> : "Subscribe"}
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <input
                                        type="text"
                                        name="symbol"
                                        value={formData.symbol}
                                        onChange={handleInputChange}
                                        placeholder="e.g. NSE:NIFTY26MAR25500CE"
                                        className="w-full bg-[#0f1116] border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-blue-500 outline-none transition-all placeholder:text-gray-600 font-mono text-sm"
                                    />
                                </div>

                                <div className="col-span-1">
                                    <label className="block text-gray-400 text-xs font-medium mb-1.5 uppercase">Arb Percentage (%) <span className="text-red-500">*</span></label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        name="arbPercentage"
                                        value={formData.arbPercentage}
                                        onChange={handleInputChange}
                                        placeholder="e.g. 1.25"
                                        className="w-full bg-[#0f1116] border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-blue-500 outline-none transition-all font-mono text-sm"
                                    />
                                </div>

                                <div className="col-span-1">
                                    <label className="block text-gray-400 text-xs font-medium mb-1.5 uppercase">Buffer Percentage (%) <span className="text-red-500">*</span></label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        name="bufferPercentage"
                                        value={formData.bufferPercentage}
                                        onChange={handleInputChange}
                                        placeholder="e.g. 0.15"
                                        className="w-full bg-[#0f1116] border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-blue-500 outline-none transition-all font-mono text-sm"
                                    />
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-gray-400 text-xs font-medium mb-1.5 uppercase">Lots (Default: 1) <span className="text-red-500">*</span></label>
                                    <input
                                        type="number"
                                        name="lots"
                                        value={formData.lots}
                                        onChange={handleInputChange}
                                        placeholder="1"
                                        min="1"
                                        className="w-full bg-[#0f1116] border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-blue-500 outline-none transition-all font-mono text-sm"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Client Selection Side */}
                    <div className="lg:w-1/2 flex flex-col h-full overflow-hidden">
                        <h3 className="text-blue-400 font-semibold text-sm uppercase tracking-wider mb-4 border-b border-blue-900/30 pb-2">Client Selection ({selectedClients.size} selected)</h3>

                        {/* Filters */}
                        <div className="flex gap-2 mb-4">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                <input
                                    type="text" placeholder="Search client..."
                                    value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full bg-[#0f1116] border border-gray-700 rounded-lg pl-9 pr-3 py-1.5 text-sm text-white"
                                />
                            </div>
                            <select
                                value={brokerFilter} onChange={(e) => setBrokerFilter(e.target.value)}
                                className="bg-[#0f1116] border border-gray-700 rounded-lg px-3 py-1.5 text-xs text-gray-300 outline-none cursor-pointer"
                            >
                                <option value="ALL">All Brokers</option>
                                <option value="FYERS">Fyers</option>
                                <option value="ZERODHA">Zerodha</option>
                                <option value="DHAN_XTS">Dhan XTS</option>
                                <option value="DHAN_XTS_ANURAG">Dhan XTS Anurag</option>
                                <option value="DHAN_HQ">Dhan HQ</option>
                                <option value="NUBRA">Nubra</option>
                            </select>
                        </div>

                        {/* Labels row */}
                        {uniqueLabels.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-4 px-1">
                                {uniqueLabels.map(label => {
                                    const labelClients = filteredClients.filter(c => c.label === label);
                                    if (labelClients.length === 0) return null;

                                    const selectedCount = labelClients.filter(c => selectedClients.has(`${c.clientId || c.client_id}_${c.broker || "fyers"}`)).length;
                                    const isAllSelected = selectedCount === labelClients.length && labelClients.length > 0;

                                    return (
                                        <button
                                            key={label}
                                            onClick={() => toggleLabelSelection(label)}
                                            className={`px-2 py-1 rounded text-[10px] font-bold uppercase transition-all border flex items-center gap-1.5 ${isAllSelected
                                                ? 'bg-blue-600 border-blue-500 text-white'
                                                : selectedCount > 0
                                                    ? 'bg-blue-900/40 border-blue-700 text-blue-200'
                                                    : 'bg-[#14161f] border-gray-700 text-gray-500 hover:border-gray-500'
                                                }`}
                                        >
                                            {label}
                                            <span className={`px-1 rounded ${isAllSelected ? 'bg-blue-800' : 'bg-gray-800 text-gray-400'}`}>
                                                {selectedCount}/{labelClients.length}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* List */}
                        <div className="flex-1 bg-[#0f1116] rounded-xl border border-gray-800 overflow-hidden flex flex-col">
                            <div className="bg-[#14161f] px-4 py-2 flex items-center justify-between border-b border-gray-800">
                                <button onClick={toggleSelectAll} className="text-[10px] text-blue-400 font-bold hover:text-blue-300 uppercase">
                                    {selectedClients.size === filteredClients.length ? "Deselect All" : "Select All Visible"}
                                </button>
                                <span className="text-[10px] text-gray-500 uppercase">{filteredClients.length} clients eligible</span>
                            </div>
                            <div className="flex-1 overflow-y-auto sleek-scrollbar">
                                {filteredClients.map((c) => {
                                    const clientId = c.clientId || c.client_id;
                                    const uniqueId = `${clientId}_${c.broker || "fyers"}`;
                                    const isSelected = selectedClients.has(uniqueId);
                                    const multiplier = c.currentMultiplier || 1;
                                    const baseLots = Number(formData.lots) || 1;
                                    const calcQty = baseLots * multiplier;

                                    return (
                                        <div
                                            key={uniqueId}
                                            onClick={() => toggleClientSelection(uniqueId)}
                                            className={`px-4 py-2 border-b border-gray-800/50 flex items-center gap-3 cursor-pointer hover:bg-blue-500/5 group transition-colors ${isSelected ? 'bg-blue-500/10' : ''}`}
                                        >
                                            <div className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${isSelected ? 'bg-blue-600 border-blue-500' : 'border-gray-600 group-hover:border-gray-400'}`}>
                                                {isSelected && <Check className="w-3.5 h-3.5 text-white" strokeWidth={4} />}
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-white text-sm font-medium">{c.name}</p>
                                                <p className="text-white text-[12px] font-mono">{clientId} | {c.broker?.toUpperCase()}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-gray-400 text-[10px] uppercase font-bold">Qty ({multiplier}x)</p>
                                                <p className="text-blue-400 text-sm font-bold font-mono">
                                                    {calcQty}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })}
                                {filteredClients.length === 0 && (
                                    <div className="p-8 text-center">
                                        <Filter className="w-10 h-10 text-gray-700 mx-auto mb-2" />
                                        <p className="text-gray-500 text-sm">No eligible clients found</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-700 flex justify-end gap-3 bg-[#14161f]">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 rounded-lg text-gray-300 hover:bg-gray-800 font-medium transition-colors"
                    >Cancel</button>
                    <button
                        onClick={handleSubmit}
                        disabled={submitting || selectedClients.size === 0}
                        className="px-8 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold transition-all shadow-lg shadow-emerald-900/40 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {submitting ? (
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        ) : null}
                        Place Arb Orders ({selectedClients.size})
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PledgePlusArbBulkOrderModal;
